import { motion } from 'framer-motion';
import { MessageSquare, Users, CalendarDays, Award } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';

const CommunityPage = () => {
  const forumPosts = [
    {
      author: 'Alex',
      role: 'Runner',
      message: 'Excited to join SportsFuture! Looking forward to connecting with fellow athletes.',
      time: '2 hours ago',
      avatar: 'https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=compress&cs=tinysrgb&h=350'
    },
    {
      author: 'Maria',
      role: 'Gymnast',
      message: 'Just received my first scholarship! Thank you to everyone who supported me.',
      time: '1 day ago',
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&h=350'
    },
    {
      author: 'Coach James',
      role: 'Swimming Coach',
      message: 'The resources provided by SportsFuture have been invaluable for my athletes.',
      time: '3 days ago',
      avatar: 'https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&h=350'
    },
    {
      author: 'Sophia',
      role: 'Basketball Player',
      message: 'Looking for training partners in the Madrid area. Anyone interested?',
      time: '5 days ago',
      avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&h=350'
    }
  ];

  const upcomingEvents = [
    {
      title: 'Virtual Athlete Meetup',
      date: 'July 15, 2025',
      description: 'Connect with athletes from around the world in our monthly virtual networking event.'
    },
    {
      title: 'Funding Workshop',
      date: 'July 22, 2025',
      description: 'Learn how to maximize your funding opportunities with expert tips and strategies.'
    },
    {
      title: 'Training Webinar',
      date: 'August 5, 2025',
      description: 'Join professional coaches for insights on optimizing your training regimen.'
    }
  ];

  return (
    <Section className="pt-28">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-5xl mx-auto"
      >
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Our Community</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join our vibrant community of athletes, supporters, and visionaries. Get involved by participating in discussions, events, and more.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden">
              <div className="bg-primary text-white p-4 flex items-center">
                <MessageSquare className="mr-2" />
                <h2 className="text-xl font-semibold">Community Forum</h2>
              </div>
              
              <div className="divide-y divide-gray-100">
                {forumPosts.map((post, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="p-4"
                  >
                    <div className="flex">
                      <img
                        src={post.avatar}
                        alt={post.author}
                        className="w-12 h-12 rounded-full mr-4 object-cover"
                      />
                      <div>
                        <div className="flex items-center mb-1">
                          <h3 className="font-medium">{post.author}</h3>
                          <span className="mx-2 text-gray-400">•</span>
                          <span className="text-primary text-sm">{post.role}</span>
                        </div>
                        <p className="text-gray-700 mb-2">{post.message}</p>
                        <p className="text-gray-500 text-sm">{post.time}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="p-4 bg-gray-50 flex justify-between items-center">
                <p className="text-gray-500">Showing recent posts</p>
                <Button variant="outline" size="sm">
                  View All
                </Button>
              </div>
            </div>
          </div>
          
          <div>
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden mb-6">
              <div className="bg-secondary text-white p-4 flex items-center">
                <CalendarDays className="mr-2" />
                <h2 className="text-xl font-semibold">Upcoming Events</h2>
              </div>
              
              <div className="divide-y divide-gray-100">
                {upcomingEvents.map((event, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="p-4"
                  >
                    <h3 className="font-medium mb-1">{event.title}</h3>
                    <p className="text-primary text-sm mb-2">{event.date}</p>
                    <p className="text-gray-600 text-sm">{event.description}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="p-4 bg-gray-50 text-center">
                <Button variant="text">
                  View All Events
                </Button>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-md sf-surface-card sf-ui-system overflow-hidden">
              <div className="bg-primary text-white p-4 flex items-center">
                <Users className="mr-2" />
                <h2 className="text-xl font-semibold">Community Stats</h2>
              </div>
              
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-700">Members:</span>
                  <span className="font-medium">2,500+</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-700">Athletes:</span>
                  <span className="font-medium">1,200+</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-700">Supporters:</span>
                  <span className="font-medium">1,300+</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Countries:</span>
                  <span className="font-medium">45+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Award className="h-16 w-16 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Become a Supporter</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Supporters can back specific athletes by contributing to their profiles. Your support
              helps talented individuals achieve their dreams and reach their full potential.
            </p>
            <Button variant="primary" size="lg">
              Join the Community
            </Button>
          </motion.div>
        </div>
      </motion.div>
    </Section>
  );
};

export default CommunityPage;